import type { DataBuilderPayload } from '../api/data-builder-api.types';
import { hydrateStoreFromPayload } from '../api/hydrate-store-from-payload';
import { buildCompaniesData } from '../components/ui/CompaniesSummary';
import type {
  DatasetSummaryData,
  DatasetSummarySetting,
} from '../components/ui/DatasetSummaryCard';
import type { ModelVersionEntry } from '../components/ui/ModelVersionsSummary';
import type {
  DeliveryFormat,
  DeliveryMethod,
  ModelVersionSelections,
  S3DeliveryConfig,
  SnowflakeDeliveryConfig,
} from '../data-builder.store';

export type ConfirmationSummaryFromPayload = {
  datasetSummaries: DatasetSummaryData[];
  deliveryMethod: DeliveryMethod;
  deliveryFormat: DeliveryFormat;
  snowflakeConfig: SnowflakeDeliveryConfig | null;
  s3Config: S3DeliveryConfig | null;
  lagData: number;
  modelVersions: ModelVersionSelections;
};

const AGGREGATION_BREAKDOWN_ID = 'aggregation';

const toTitleCase = (key: string) =>
  key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const toSettingsFromEntries = (
  entries: [string, unknown][]
): DatasetSummarySetting[] =>
  entries.map(([key, value]) => ({
    label: toTitleCase(key),
    displayValue: String(value),
  }));

const toModelVersionEntries = (
  entries: [string, unknown][]
): ModelVersionEntry[] =>
  entries.map(([key, value]) => ({
    modelLabel: toTitleCase(key),
    versionLabel: String(value),
  }));

export function buildConfirmationSummaryFromPayload(
  payload: DataBuilderPayload
): ConfirmationSummaryFromPayload {
  const hydrated = hydrateStoreFromPayload(payload);

  const modelVersionEntries = Object.entries(hydrated.modelVersions);

  const datasetSummaries: DatasetSummaryData[] = hydrated.datasetInstances.map(
    (instance) => {
      const label = instance.customLabel || instance.datasetKey;
      const config = hydrated.configByInstance[instance.instanceId];
      const companyEntries = Object.entries(config?.companySelections ?? {});
      const allColumnEntries = Object.entries(
        config?.columnKeysByBreakdown ?? {}
      );
      // If at least one breakdown has selections, hide breakdowns with empty
      // selections so the status modal doesn't render rows for unselected files.
      // If ALL breakdowns are empty, keep them so DatasetSummaryCard's
      // "Default" fallback (allColumns.length === 0) still triggers.
      const hasAnySelections = allColumnEntries.some(
        ([, keys]) => keys.length > 0
      );
      const columnEntries = hasAnySelections
        ? allColumnEntries.filter(([, keys]) => keys.length > 0)
        : allColumnEntries;
      const topLevelFilters = config?.customFilters ?? [];
      const breakdownFilterEntries = Object.entries(
        config?.customFiltersByBreakdown ?? {}
      );
      const customColumnEntries = Object.entries(
        config?.customColumnsByBreakdown ?? {}
      );

      const emptyMap = {} as Record<string, string>;

      // Sort columns: aggregation first, then file breakdowns
      const sortedColumns = columnEntries
        .map(([breakdownId, keys]) => ({
          breakdownId,
          breakdownLabel: toTitleCase(breakdownId),
          columns: keys.map((key) => ({ key, label: toTitleCase(key) })),
        }))
        .sort((a, b) => {
          if (a.breakdownId === AGGREGATION_BREAKDOWN_ID) return -1;
          if (b.breakdownId === AGGREGATION_BREAKDOWN_ID) return 1;
          return 0;
        });

      // Collect file breakdown labels as granularities
      const granularities = columnEntries
        .filter(([id]) => id !== AGGREGATION_BREAKDOWN_ID)
        .map(([id]) => toTitleCase(id));
      const pipelineInputTable = config?.companyMapper?.pipeline_input || null;

      return {
        instanceId: instance.instanceId,
        datasetKey: instance.datasetKey,
        label,
        unitOfAnalysis: config?.unitOfAnalysis ?? 'default',
        unitOfAnalysisSelections: config?.unitOfAnalysisSelections ?? [],
        companies: buildCompaniesData(
          config?.companyMapper,
          config?.companySets,
          companyEntries
        ),
        pipelineInputTable,
        granularities,
        columns: sortedColumns,
        filterGroups: [
          ...(topLevelFilters.length > 0
            ? [
                {
                  breakdownLabel: null,
                  filters: topLevelFilters,
                  fieldLabelMap: emptyMap,
                  fieldGranularityMap: emptyMap,
                },
              ]
            : []),
          ...breakdownFilterEntries
            .filter(([, filters]) => filters.length > 0)
            .map(([breakdownId, filters]) => ({
              breakdownLabel: toTitleCase(breakdownId),
              filters,
              fieldLabelMap: emptyMap,
              fieldGranularityMap: emptyMap,
            })),
        ],
        customColumnGroups: customColumnEntries
          .filter(([, columns]) => columns.length > 0)
          .map(([breakdownId, columns]) => ({
            breakdownLabel: toTitleCase(breakdownId),
            customColumns: columns,
            fieldLabelMap: emptyMap,
            fieldGranularityMap: emptyMap,
          })),
        settings: toSettingsFromEntries(Object.entries(config?.settings ?? {})),
        modelVersions: toModelVersionEntries(modelVersionEntries),
        allLatestModelVersions: modelVersionEntries.length === 0,
      };
    }
  );

  return {
    datasetSummaries,
    deliveryMethod: hydrated.deliveryMethod,
    deliveryFormat: hydrated.deliveryFormat,
    snowflakeConfig: hydrated.snowflakeConfig,
    s3Config: hydrated.s3Config,
    lagData: hydrated.lagData,
    modelVersions: hydrated.modelVersions,
  };
}
